import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format, addDays } from "date-fns";
import { Plus, Pencil, Trash2, RefreshCw, Users, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get stored credentials from sessionStorage
const getAuthHeader = () => {
  const creds = sessionStorage.getItem('lyca_auth');
  if (creds) {
    return { Authorization: `Basic ${creds}` };
  }
  return {};
};

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    lyca_mobile_number: "",
    plan_type_id: "",
    plan_price: 0,
    notes: "",
    next_renewal_date: addDays(new Date(), 27),
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeader();
      const [customersRes, planTypesRes] = await Promise.all([
        axios.get(`${API}/customers`, { headers }),
        axios.get(`${API}/plan-types`, { headers }),
      ]);
      setCustomers(customersRes.data);
      setPlanTypes(planTypesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setSelectedCustomer(customer);
      setFormData({
        name: customer.name,
        lyca_mobile_number: customer.lyca_mobile_number,
        plan_type_id: customer.plan_type_id,
        plan_price: customer.plan_price,
        notes: customer.notes || "",
        next_renewal_date: new Date(customer.next_renewal_date),
      });
    } else {
      setSelectedCustomer(null);
      setFormData({
        name: "",
        lyca_mobile_number: "",
        plan_type_id: planTypes[0]?.id || "",
        plan_price: planTypes[0]?.default_price || 0,
        notes: "",
        next_renewal_date: addDays(new Date(), 27),
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = getAuthHeader();
      const payload = {
        ...formData,
        next_renewal_date: formData.next_renewal_date.toISOString(),
      };

      if (selectedCustomer) {
        await axios.put(`${API}/customers/${selectedCustomer.id}`, payload, { headers });
        toast.success("Customer updated successfully");
      } else {
        await axios.post(`${API}/customers`, payload, { headers });
        toast.success("Customer created successfully");
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error(error.response?.data?.detail || "Failed to save customer");
    }
  };

  const handleDelete = async () => {
    try {
      const headers = getAuthHeader();
      await axios.delete(`${API}/customers/${selectedCustomer.id}`, { headers });
      toast.success("Customer deleted successfully");
      setIsDeleteDialogOpen(false);
      setSelectedCustomer(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error("Failed to delete customer");
    }
  };

  const handlePlanTypeChange = (planTypeId) => {
    const planType = planTypes.find((pt) => pt.id === planTypeId);
    setFormData({
      ...formData,
      plan_type_id: planTypeId,
      plan_price: planType?.default_price || formData.plan_price,
    });
  };

  const filteredCustomers = customers.filter(
    (c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.lyca_mobile_number.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 text-[#00D26A] animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="customers-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-white" data-testid="customers-title">
            Customers
          </h1>
          <p className="text-slate-400 mt-1">Manage your Lyca Mobile customers</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="btn-primary flex items-center gap-2"
          data-testid="add-customer-btn"
        >
          <Plus className="w-4 h-4" />
          Add Customer
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            type="text"
            placeholder="Search by name or mobile number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 form-input"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Customers Table */}
      <div className="card" data-testid="customers-table-section">
        <div className="card-content p-0">
          {filteredCustomers.length === 0 ? (
            <div className="empty-state py-12">
              <Users className="w-12 h-12 text-slate-600 mb-4" />
              <p className="text-lg">No customers found</p>
              <p className="text-sm">
                {customers.length === 0
                  ? "Add your first customer to get started."
                  : "Try a different search term."}
              </p>
            </div>
          ) : (
            <table className="data-table" data-testid="customers-table">
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Mobile Number</th>
                  <th>Plan Type</th>
                  <th>Price</th>
                  <th>Next Renewal</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} data-testid={`customer-row-${customer.id}`}>
                    <td className="font-medium text-white">{customer.name}</td>
                    <td className="font-mono">{customer.lyca_mobile_number}</td>
                    <td>{customer.plan_type_name}</td>
                    <td className="font-mono">€{customer.plan_price.toFixed(2)}</td>
                    <td>{format(new Date(customer.next_renewal_date), "dd MMM yyyy")}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleOpenDialog(customer)}
                          className="p-2 hover:bg-slate-800 rounded transition-colors"
                          data-testid={`edit-customer-${customer.id}`}
                        >
                          <Pencil className="w-4 h-4 text-[#0066FF]" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setIsDeleteDialogOpen(true);
                          }}
                          className="p-2 hover:bg-slate-800 rounded transition-colors"
                          data-testid={`delete-customer-${customer.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {selectedCustomer ? "Edit Customer" : "Add New Customer"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-slate-300">Customer Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="form-input mt-1"
                required
                data-testid="input-name"
              />
            </div>
            <div>
              <Label htmlFor="mobile" className="text-slate-300">Lyca Mobile Number</Label>
              <Input
                id="mobile"
                value={formData.lyca_mobile_number}
                onChange={(e) => setFormData({ ...formData, lyca_mobile_number: e.target.value })}
                className="form-input mt-1"
                placeholder="+353..."
                required
                data-testid="input-mobile"
              />
            </div>
            <div>
              <Label htmlFor="plan_type" className="text-slate-300">Plan Type</Label>
              <Select
                value={formData.plan_type_id}
                onValueChange={handlePlanTypeChange}
              >
                <SelectTrigger className="form-input mt-1" data-testid="select-plan-type">
                  <SelectValue placeholder="Select plan type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {planTypes.map((pt) => (
                    <SelectItem key={pt.id} value={pt.id} className="text-slate-200 hover:bg-slate-800">
                      {pt.name} - €{pt.default_price.toFixed(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="price" className="text-slate-300">Plan Price (€)</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.plan_price}
                onChange={(e) => setFormData({ ...formData, plan_price: parseFloat(e.target.value) || 0 })}
                className="form-input mt-1"
                data-testid="input-price"
              />
            </div>
            <div>
              <Label className="text-slate-300">Next Renewal Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full mt-1 form-input justify-start text-left font-normal"
                    data-testid="date-picker-trigger"
                  >
                    {format(formData.next_renewal_date, "dd MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-800" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.next_renewal_date}
                    onSelect={(date) => date && setFormData({ ...formData, next_renewal_date: date })}
                    initialFocus
                    className="bg-slate-900"
                    data-testid="calendar"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label htmlFor="notes" className="text-slate-300">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="form-input mt-1"
                rows={2}
                data-testid="input-notes"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsDialogOpen(false)}
                className="btn-ghost"
                data-testid="cancel-btn"
              >
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-btn">
                {selectedCustomer ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-800" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Customer</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Are you sure you want to delete &quot;{selectedCustomer?.name}&quot;? This action cannot be undone and will also delete all associated renewal tasks.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-ghost" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="btn-danger" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
